import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import {
    DatePickerContainer,
    DatePickerDiv,
    DatePickerIcon,
    StyledClearSpan,
    StyledDiv,
    StyledSpan,
    DatePopover,
} from './DatePicker.styled';
import { Dropdown } from 'primereact/dropdown';
import CalendarInactive from 'src/assets/images/Menu/Calendar_Inactive.svg';
import { getDisplayDate } from 'src/utils/dateUtils';
import { useTranslation } from 'react-i18next';
import LeftArrow from 'src/assets/images/BaselineKeyboardArrowLeft24.svg';
import RightArrow from 'src/assets/images/BaselineKeyboardArrowRight24.svg';
import DownArrowMonthDropDown from 'src/assets/images/DownArrowMonthDropDown.svg';
import { dateSelectorPopUpOpen } from './handlers/dateSelectorPopUpOpen';
import { disableNextButton } from './handlers/disableNextButton';
import { logErrorToConsole } from 'src/utils/util';
import { toggleNextButton } from './handlers/toggleNextButton';
import { dateSelectorPopUpClose } from './handlers/dateSelectorPopUpClose';
import { clearSelectedDate } from './handlers/clearSelectedDate';
import { useCalendarPopoverClickHandler } from './hooks/useCalendarPopoverClickHandler';
import { monthNames } from './handlers/monthNames';
import { monthNavigatorTemplate as monthNavigatorTemplateHandler } from './handlers/monthNavigatorTemplate';
import { yearNavigatorTemplate as yearNavigatorTemplateHandler } from './handlers/yearNavigatorTemplate';
import { DATE_SELECT, DATE_UNSELECT, DEFAULT_YEAR_RANGE, OPACITY, OPACITY_DULL, WIDTH } from '../../../constants/appConstants';

const DatePicker = ({
    datepickerItem,
    placeholder,
    disableWeekEnds,
    enableMinDate = '',
    onChange,
    selectedDateFromDatePicker,
    datePickerCalendarIcon,
    datePickerDivWidth,
    borderRadius,
    datePickerDivHeight,
    minDate,
    sameDayRdd,
    maxDate,
    datePickerDivMbWidth,
    datePickerDivMbHeight,
    iconHeight,
    fontSize,
    disabled,
    left,
    opacity,
    datePickerBgColor,
    border,
    datePopOverZIndex,
    yearRange,
    iconPaddingTop,
    iconRight,
    reportUsage,
    textOpacity,
    smbWidth,
    smbIconPaddingTop,
    smbIconheight,
    rdd,
    mbIconRight,
    minWidth,
    id,
    panelWidth,
    dropdownPanelWidth,
    defaultClearDate,
    isFim
}) => {
    // const datePopoverRef = useRef();
  
    const monthDDRef = useRef();
    const yearDDRef = useRef();
    const { t } = useTranslation();
    const [topY, setTopY] = useState();
    const [dateSelectorPopUpAnchorEl, setDateSelectorPopUpAnchorEl] = useState(null);
    const [disablePrevMonth, setDisablePrevMonth] = useState(false);
    const [selectedYear, setSelecterYear] = useState(0);
    const [viewDate, setViewDate] = useState(null);

    const disabledDates = useMemo(
        () => (Array.isArray(enableMinDate) ? enableMinDate : []),
        [enableMinDate]
    );

    const disabledDays = useMemo(
        () => (Array.isArray(disableWeekEnds) ? disableWeekEnds : []),
        [disableWeekEnds]
    );

    const normalizeToStartOfDay = useCallback((date) => {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
    }, []);

    const getInitialViewDate = useCallback(() => {
        const min = minDate ? normalizeToStartOfDay(minDate) : null;
        const max = maxDate ? normalizeToStartOfDay(maxDate) : null;

        const disabledDateMsSet = new Set(
            (disabledDates || []).map((d) => normalizeToStartOfDay(d).getTime())
        );
        const disabledDaySet = new Set(disabledDays || []);

        const isSelectable = (candidate) => {
            const dayStart = normalizeToStartOfDay(candidate);
            if (min && dayStart < min) return false;
            if (max && dayStart > max) return false;
            if (disabledDaySet.has(dayStart.getDay())) return false;
            if (disabledDateMsSet.has(dayStart.getTime())) return false;
            return true;
        };

        const selected = selectedDateFromDatePicker
            ? normalizeToStartOfDay(selectedDateFromDatePicker)
            : null;
        if (selected && isSelectable(selected)) {
            return selected;
        }

        const today = normalizeToStartOfDay(new Date());

        const clampToRange = (candidate) => {
            let clamped = normalizeToStartOfDay(candidate);
            if (min && clamped < min) clamped = new Date(min);
            if (max && clamped > max) clamped = new Date(max);
            return clamped;
        };

        const monthHasSelectableDay = (dateInMonth) => {
            const year = dateInMonth.getFullYear();
            const month = dateInMonth.getMonth();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            for (let day = 1; day <= daysInMonth; day += 1) {
                if (isSelectable(new Date(year, month, day))) return true;
            }
            return false;
        };

        // Prefer showing the current month (clamped to min/max) when it contains any selectable day.
        // This avoids jumping to a different month just because it has an earlier selectable date.
        const anchor = clampToRange(today);
        if (monthHasSelectableDay(anchor)) {
            return anchor;
        }

        const DAY_MS = 24 * 60 * 60 * 1000;
        const daysBetween = (a, b) => {
            const start = normalizeToStartOfDay(a).getTime();
            const end = normalizeToStartOfDay(b).getTime();
            return Math.max(0, Math.ceil((end - start) / DAY_MS));
        };

        const maxForwardIterations = max ? daysBetween(anchor, max) : 0;
        const maxBackwardIterations = min ? daysBetween(min, anchor) : 0;

        const search = (direction, maxIterations) => {
            for (let i = 0; i <= maxIterations; i += 1) {
                const candidate = new Date(anchor);
                candidate.setDate(candidate.getDate() + direction * i);

                if (min && candidate < min) {
                    if (direction < 0) break;
                    continue;
                }
                if (max && candidate > max) {
                    if (direction > 0) break;
                    continue;
                }

                if (isSelectable(candidate)) return candidate;
            }
            return null;
        };

        // Forward search first; if nothing is selectable forward, try backward.
        return search(1, maxForwardIterations) || search(-1, maxBackwardIterations) || anchor;
    }, [selectedDateFromDatePicker, minDate, maxDate, disabledDates, disabledDays]);


    // Use ref to always have the latest maxDate value (avoids stale closure)
    const maxDateRef = useRef(maxDate);
    maxDateRef.current = maxDate;

    const handleToggleNextButton = useCallback(() => {
        toggleNextButton(maxDateRef.current, monthDDRef, yearDDRef, monthNames);
    }, [maxDateRef, monthDDRef, yearDDRef, monthNames]);

    const clampToRange = useCallback(
        (candidate) => {
            let clamped = normalizeToStartOfDay(candidate);
            const min = minDate ? normalizeToStartOfDay(minDate) : null;
            const max = maxDate ? normalizeToStartOfDay(maxDate) : null;
            if (min && clamped < min) clamped = new Date(min);
            if (max && clamped > max) clamped = new Date(max);
            return clamped;
        },
        [minDate, maxDate, normalizeToStartOfDay]
    );

    const handleMonthDropdownChange = useCallback(
        (newMonth) => {
            setViewDate((prev) => {
                const base = prev instanceof Date && !isNaN(prev) ? new Date(prev) : new Date();
                const year = base.getFullYear();
                const day = base.getDate();
                const daysInTargetMonth = new Date(year, newMonth + 1, 0).getDate();
                const next = new Date(year, newMonth, Math.min(day, daysInTargetMonth));
                return clampToRange(next);
            });
        },
        [clampToRange]
    );

    const handleYearDropdownChange = useCallback(
        (newYear) => {
            setViewDate((prev) => {
                const base = prev instanceof Date && !isNaN(prev) ? new Date(prev) : new Date();
                const month = base.getMonth();
                const day = base.getDate();
                const daysInTargetMonth = new Date(newYear, month + 1, 0).getDate();
                const next = new Date(newYear, month, Math.min(day, daysInTargetMonth));
                return clampToRange(next);
            });
        },
        [clampToRange]
    );

    const handleDateSelectorPopUpOpen = useCallback((event) => {
        // RDD needs custom initial month selection; others open on selected date (or today).
        if (rdd) {
            setViewDate(getInitialViewDate());
        } else {
            const base = selectedDateFromDatePicker ? normalizeToStartOfDay(selectedDateFromDatePicker) : normalizeToStartOfDay(new Date());
            setViewDate(clampToRange(base));
        }
        dateSelectorPopUpOpen(event, setTopY, setDateSelectorPopUpAnchorEl, disabled, () => handleToggleNextButton());
    }, [setTopY, setDateSelectorPopUpAnchorEl, disabled, handleToggleNextButton, getInitialViewDate, rdd, selectedDateFromDatePicker, normalizeToStartOfDay, clampToRange]);

    const handleDateSelectorPopUpClose = useCallback(() => {
        dateSelectorPopUpClose(setDateSelectorPopUpAnchorEl);
    }, [setDateSelectorPopUpAnchorEl]);
    const isDateSelectorPopOverOpen = Boolean(dateSelectorPopUpAnchorEl);
    const dateSelectorPopOverId = isDateSelectorPopOverOpen ? 'simple-popover' : undefined;
    const calendarRef = useRef();
    useCalendarPopoverClickHandler(calendarRef, handleDateSelectorPopUpClose);
    const handleClearSelectedDate = useCallback(() => clearSelectedDate(onChange, defaultClearDate), [onChange, defaultClearDate]);
    const monthNavigatorTemplate = useCallback(
        (e) => monthNavigatorTemplateHandler(e, rdd, selectedYear, setDisablePrevMonth, monthDDRef, dropdownstyle, handleMonthDropdownChange),
        [rdd, selectedYear, setDisablePrevMonth, monthDDRef, dropdownstyle, handleMonthDropdownChange]
    );

    const yearNavigatorTemplate = useCallback(
        (e) => yearNavigatorTemplateHandler(e, setSelecterYear, handleClearSelectedDate, t, yearDDRef, handleYearDropdownChange),
        [setSelecterYear, handleClearSelectedDate, t, yearDDRef, handleYearDropdownChange]
    );

    const dropdownstyle = useMemo(() => ({ width: WIDTH, lineHeight: 1 }), []);

    return (
        <>
            <DatePickerDiv
                id={id}
                minWidth={minWidth}
                onClick={disabled ? undefined : handleDateSelectorPopUpOpen}
                aria-describedby={dateSelectorPopOverId}
                width={datePickerDivWidth}
                sameDayRdd={sameDayRdd}
                height={datePickerDivHeight}
                mbWidth={datePickerDivMbWidth}
                mbHeight={datePickerDivMbHeight}
                backgroundColor={datePickerBgColor}
                fontSize={fontSize}
                border={border}
                opacity={opacity}
                disabled={disabled}
                reportUsage={reportUsage}
                smbWidth={smbWidth}
                borderRadius={borderRadius}
                tabIndex={disabled ? -1 : 0}
                style={disabled ? { pointerEvents: 'none' } : {}}
            >
                <StyledDiv
                    left={left}
                    opacity={opacity ===OPACITY_DULL ? OPACITY : opacity}
                    reportUsage={reportUsage}
                    textOpacity={textOpacity}
                    placeholderColor={selectedDateFromDatePicker ? DATE_SELECT : DATE_UNSELECT}
                >
                    {(selectedDateFromDatePicker !== null && selectedDateFromDatePicker !== undefined && selectedDateFromDatePicker !== '') ? getDisplayDate(selectedDateFromDatePicker) : placeholder}
                </StyledDiv>
                <StyledSpan
                    iconPaddingTop={iconPaddingTop}
                    iconRight={iconRight}
                    mbIconRight={mbIconRight}
                    smbIconPaddingTop={smbIconPaddingTop}
                >
                    <DatePickerIcon
                        defaultImg={datePickerCalendarIcon || CalendarInactive}
                        name="calendra-icon"
                        height={iconHeight}
                        smbIconheight={smbIconheight}
                    />
                </StyledSpan>
            </DatePickerDiv>
            <DatePopover
                id={dateSelectorPopOverId}
                open={isDateSelectorPopOverOpen}
                anchorEl={dateSelectorPopUpAnchorEl}
                onClose={handleDateSelectorPopUpClose}
                anchorOrigin={isFim ? { vertical: 'top', horizontal: 'right' } : { vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={isFim ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'top', horizontal: 'center' }}
                style={{ zIndex: datePopOverZIndex || 10000000 }}
                dismissable="true"
                top={dateSelectorPopUpAnchorEl ? topY : null}
                ref={calendarRef}
                panelWidth={panelWidth}
                isFim={isFim}
            >
                <DatePickerContainer
                    leftArrow={LeftArrow}
                    rightArrow={RightArrow}
                    disableLeftArrow={disablePrevMonth}
                    downArrowMonthDropDown={DownArrowMonthDropDown}
                    onClick={handleToggleNextButton}
                    dropdownPanelWidth={dropdownPanelWidth}
                >
                    <Calendar
                        disabled={disabled}
                        inline
                        id={`${datepickerItem}`}
                        placeholder={`${placeholder}`}
                        value={selectedDateFromDatePicker || null}
                        viewDate={viewDate || undefined}
                        onViewDateChange={
                            (e) => {
                                try {
                                    setViewDate(clampToRange(e.value));
                                } catch (error) {
                                    logErrorToConsole(error);
                                }
                            }
                        }
                        onChange={e => {
                            try {
                                onChange(e.value);
                                handleDateSelectorPopUpClose();
                            } catch (error) {
                                logErrorToConsole(error);
                            }
                        }}
                        showIcon
                        showButtonBar={false}
                        monthNavigator
                        yearNavigator
                        yearRange={yearRange || DEFAULT_YEAR_RANGE}
                        minDate={minDate}
                        disabledDates={disabledDates}
                        disabledDays={disableWeekEnds}
                        maxDate={maxDate}
                        monthNavigatorTemplate={monthNavigatorTemplate}
                        yearNavigatorTemplate={yearNavigatorTemplate}
                    />
                </DatePickerContainer>
            </DatePopover>
        </>
    );
};

export default DatePicker;
